import { useEffect, useState } from "react";
import axios from "axios";
import { Activity, Server, Zap, Cpu, MessageSquareText } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOTAL_TOKENS = 100_000_000;

export default function DigitalTwin() {
  const { t } = useI18n();
  const [twin, setTwin] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/twin`, { timeout: 4000 });
        if (!cancelled) setTwin(r.data);
      } catch {
        if (!cancelled) {
          setTwin({
            servers_online: 6,
            servers_total: 6,
            uptime_pct: 99.94,
            tokens_used: Math.floor(38_000_000 + Math.random() * 4_000_000),
            tokens_total: TOTAL_TOKENS,
            latency_ms: 45 + Math.floor(Math.random() * 20),
            active_threads: 120 + Math.floor(Math.random() * 40),
            generated_at: new Date().toISOString(),
          });
        }
      }
    };
    tick();
    const id = setInterval(tick, 3500);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const used = twin?.tokens_used || 0;
  const total = twin?.tokens_total || TOTAL_TOKENS;
  const pct = Math.min(100, Math.max(0, (used / total) * 100));
  const remaining = total - used;
  const formatTokens = (n) =>
    n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n.toLocaleString("fr-FR");

  return (
    <div
      data-testid="digital-twin"
      className="dncm-card rounded-2xl p-5 sm:p-6 relative overflow-hidden"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <span className="dncm-pulse-dot" style={{ background: "var(--dncm-green)", boxShadow: "0 0 12px var(--dncm-green)" }} />
            <span className="font-mono-d text-[11px] uppercase tracking-[0.32em] text-white/65">
              {t("twin_subtitle")}
            </span>
          </div>
          <h3 className="mt-2 font-display text-2xl sm:text-3xl text-white">
            {t("twin_title")}
          </h3>
        </div>
        <div className="hidden sm:flex items-center gap-2 font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/50">
          {twin?.generated_at ? new Date(twin.generated_at).toLocaleTimeString() : "—"}
          <span className="text-[color:var(--dncm-cyan)]">live</span>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile
          testid="twin-servers"
          icon={<Server className="w-3.5 h-3.5" />}
          label={t("twin_servers")}
          value={twin ? `${twin.servers_online}/${twin.servers_total}` : "—"}
        />
        <Tile
          testid="twin-uptime"
          icon={<Activity className="w-3.5 h-3.5" />}
          label={t("twin_uptime")}
          value={twin ? `${twin.uptime_pct}%` : "—"}
        />
        <Tile
          testid="twin-latency"
          icon={<Zap className="w-3.5 h-3.5" />}
          label={t("twin_latency")}
          value={twin ? `${twin.latency_ms}ms` : "—"}
        />
        <Tile
          testid="twin-threads"
          icon={<MessageSquareText className="w-3.5 h-3.5" />}
          label={t("twin_threads")}
          value={twin ? twin.active_threads : "—"}
        />
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/55">
          <span className="inline-flex items-center gap-2">
            <Cpu className="w-3.5 h-3.5 text-[color:var(--dncm-cyan)]" />
            {t("twin_tokens_used")}
          </span>
          <span className="text-white">
            {formatTokens(used)} <span className="text-white/40">/ {formatTokens(total)}</span>
          </span>
        </div>
        <div className="mt-2 h-2 rounded-full overflow-hidden bg-white/5 border border-white/10">
          <div
            className="h-full"
            style={{
              width: `${pct}%`,
              background: "linear-gradient(90deg, #00cfff 0%, #deff9a 100%)",
              boxShadow: "0 0 16px rgba(0,207,255,0.45)",
            }}
          />
        </div>
        <div className="mt-1.5 font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/45">
          {t("twin_tokens_left")}: <span className="text-[color:var(--dncm-green)]">{formatTokens(remaining)}</span>
        </div>
      </div>
    </div>
  );
}

function Tile({ icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-white/10 bg-black/40 p-3">
      <div className="flex items-center gap-2 font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/55">
        {icon}
        {label}
      </div>
      <div className="mt-1 font-display text-xl text-white">{value}</div>
    </div>
  );
}
